import { M4, V4 } from '@moleculeone/utils';
import { atomToNeighborsPairs } from './selectors';
import { commonKeys } from './collections';

export function alignMatrix(positions, target, keys) {
  const positionsCenter = V4.average(...keys.map(k => positions[k]));
  const targetCenter = V4.average(...keys.map(k => target[k]));

  let exx = 0;
  let exy = 0;
  let eyx = 0;
  let eyy = 0;

  for (const key of keys) {
    // we're scaling down vectors so their norms will mostly fit in [0, 20) range
    // (100 is avg bond length), so we won't get any overflows after summing them up
    const oldpos = V4.scale(V4.sub(target[key], targetCenter), 1 / 100);
    const newpos = V4.scale(V4.sub(positions[key], positionsCenter), 1 / 100);
    exx += newpos[0] * oldpos[0];
    exy += newpos[0] * oldpos[1];
    eyx += newpos[1] * oldpos[0];
    eyy += newpos[1] * oldpos[1];
  }

  let alpha = Math.atan2(exy - eyx, exx + eyy);
  if (!isMinimum(exx, exy, eyy, eyx, alpha)) {
    alpha += Math.PI;
  }

  return M4.mulM(M4.moveTo(targetCenter), M4.rotZ(alpha), M4.moveFrom(positionsCenter));
}

function isMinimum(exx, exy, eyy, eyx, alpha) {
  return Math.cos(alpha) * (exx + eyy) > Math.sin(alpha) * (eyx - exy);
}

export function flipXPositions(positions) {
  const result = {};
  for (const [key, [x, y, z]] of Object.entries(positions)) {
    result[key] = [-x, y, z];
  }
  return result;
}

export function flipAlongAxis(positions, axis = V4.vectx) {
  const center = V4.average(...Object.values(positions));
  return flipAlongSegment(positions, center, V4.add(center, axis));
}

export function flipAlongSegment(positions, start, end) {
  const mat = flipAlongSegmentMatrix(start, end);
  return M4.map(mat, positions);
}

export function flipAlongSegmentMatrix(start, end) {
  const axis = V4.sub(end, start);
  const perpendicular = V4.rotZ(axis, Math.PI * 0.5);
  return M4.mulM(M4.moveTo(start), M4.scaleVS(perpendicular, -1), M4.moveFrom(start));
}

export function align(positions, target) {
  const keys = commonKeys(positions, target);
  const matrix = alignMatrix(positions, target, keys);
  return M4.map(matrix, positions);
}

export function alignFlip(positions, target) {
  const candidate1 = align(positions, target);
  const candidate2 = align(flipXPositions(positions), target);
  if (distSquareSum(candidate1, target) < distSquareSum(candidate2, target)) {
    return candidate1;
  }
  return candidate2;
}

export function distSquareSum(pos1, pos2) {
  const keys = commonKeys(pos1, pos2);
  let sumOfDistSquares = 0;
  for (const key of keys) {
    sumOfDistSquares += V4.distsqr(pos1[key], pos2[key]);
  }
  return sumOfDistSquares;
}

export function rotateAroundBond(mol, flipIdx, fixedIdx, positions = mol.positions) {
  // A     B    A     B
  //  \   /      \   /
  //   fxd        fxd
  //    |    ==>   |
  //  flipd      flipd
  //  /   \      /   \
  // X     Y    Y     X
  const flippedPositions = flipAlongSegment(positions, positions[flipIdx], positions[fixedIdx]);

  const neighbors = atomToNeighborsPairs(mol);
  const idxToNeighbors = idx => neighbors[idx].map(([b, a]) => a.idx);
  const flippedDistMap = buildGraphDists([flipIdx], [fixedIdx], idxToNeighbors);
  const fixedDistMap = buildGraphDists([fixedIdx], [flipIdx], idxToNeighbors);

  const newPositions = {};
  for (const [atomIdx, oldPosition] of Object.entries(positions)) {
    const distFromFlipped = flippedDistMap[atomIdx];
    const distFromFixed = fixedDistMap[atomIdx];
    if (distFromFlipped == null || distFromFlipped >= distFromFixed) {
      newPositions[atomIdx] = oldPosition;
    } else {
      newPositions[atomIdx] = flippedPositions[atomIdx];
    }
  }

  return newPositions;
}

export function buildGraphDists(oneDistIndexes, zeroDistIndexes, idxToNeighbors) {
  const distMap = {};
  zeroDistIndexes.forEach(zero => (distMap[zero] = 0));

  let dist = 1;
  let waiting = oneDistIndexes;
  while (waiting.length > 0) {
    const nextWaiting = [];
    for (const next of waiting) {
      if (distMap[next] == null) {
        distMap[next] = dist;
        idxToNeighbors(next).forEach(idx => nextWaiting.push(idx));
      }
    }
    waiting = nextWaiting;
    dist += 1;
  }
  return distMap;
}
